import { useMemo, useState } from "react";

import { Button } from "@/design-system";
import { usePasswordResetConfirmMutation } from "@/api/queries";
import { useScreenTitle } from "@/lib/use-screen-title";

import { AuthLayout } from "./AuthLayout";
import { Field } from "./Field";

const PASSWORD_RE = /^(?=.*[A-Za-z])(?=.*\d).{8,128}$/;

export interface ResetPasswordProps {
  token: string | null;
  onLogin: () => void;
  onHome: () => void;
}

export function ResetPassword({ token, onLogin, onHome }: ResetPasswordProps) {
  useScreenTitle("Scegli una nuova password");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const confirmMutation = usePasswordResetConfirmMutation();

  const passwordError = useMemo(() => {
    if (!submitted || password === "") return null;
    if (!PASSWORD_RE.test(password))
      return "Almeno 8 caratteri, una lettera e un numero.";
    return null;
  }, [password, submitted]);

  const confirmError = useMemo(() => {
    if (!submitted || confirm === "") return null;
    if (confirm !== password) return "Le due password non coincidono.";
    return null;
  }, [confirm, password, submitted]);

  if (!token) {
    return (
      <AuthLayout title="Link non valido" onBack={onHome}>
        <p className="text-[14px] leading-[1.55] text-ink-muted mb-5">
          Il link di reset non è completo. Richiedine uno nuovo dalla pagina di
          login.
        </p>
        <Button variant="primary" onClick={onLogin}>
          Vai al login
        </Button>
      </AuthLayout>
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (!PASSWORD_RE.test(password) || password !== confirm) return;
    confirmMutation.mutate({ token: token!, password, password_confirm: confirm });
  }

  if (confirmMutation.isSuccess) {
    return (
      <AuthLayout
        title="Password aggiornata"
        subtitle="Ora puoi accedere con la nuova password."
        onBack={onHome}
      >
        <Button variant="primary" full onClick={onLogin}>
          Accedi
        </Button>
      </AuthLayout>
    );
  }

  const formError = confirmMutation.isError
    ? mapResetError(confirmMutation.error)
    : null;

  return (
    <AuthLayout
      title="Scegli una nuova password"
      subtitle="Useremo questa password per i prossimi accessi."
      onBack={onHome}
    >
      <form onSubmit={handleSubmit} noValidate>
        <Field
          label="Nuova password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="Almeno 8 caratteri, una lettera e un numero."
          error={passwordError}
          required
        />
        <Field
          label="Conferma password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={confirmError}
          required
        />
        {formError && (
          <p className="text-[13px] text-brick-danger mb-3">{formError}</p>
        )}
        <Button
          type="submit"
          variant="primary"
          full
          disabled={confirmMutation.isPending}
        >
          {confirmMutation.isPending ? "Salvo…" : "Salva password"}
        </Button>
      </form>
    </AuthLayout>
  );
}

function mapResetError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (msg.includes("token_expired")) return "Il link è scaduto. Richiedine uno nuovo.";
  if (msg.includes("invalid_or_used_token"))
    return "Questo link non è più valido o è stato già usato.";
  return "Qualcosa è andato storto. Riprova fra qualche istante.";
}
